/**
 * Client-Side HEIC to JPEG Conversion Utility
 *
 * Converts HEIC/HEIF images to JPEG in the browser before upload.
 * Uses libheif-js (via heic-decode) which supports ALL HEIC profiles
 * including MiPr (HEVC Main Progressive) from iOS 18+ / iPhone 14+.
 *
 * This eliminates the need for server-side dependencies and works
 * on any hosting platform including AWS Elastic Beanstalk.
 *
 * Author: Assistant
 * Date: 2025-01-15
 * Updated: 2025-10-01 - Migrated from heic2any to heic-decode for MiPr support
 */

import decode from 'heic-decode'

export interface ConversionResult {
  file: File
  wasConverted: boolean
  originalName: string
  originalSize: number
  convertedSize?: number
}

/**
 * Reads the first N bytes of a file for magic byte inspection
 */
async function readFileHeader(file: File, byteCount: number): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    const blob = file.slice(0, byteCount)

    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer
      resolve(new Uint8Array(arrayBuffer))
    }

    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(blob)
  })
}

/**
 * Checks if file content has HEIC magic bytes
 *
 * HEIC files have the following structure:
 * - Bytes 4-8: "ftyp" (file type box marker)
 * - Bytes 8-12: Brand identifier (heic, heix, hevc, hevx, mif1, msf1)
 */
function hasHeicMagicBytes(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false

  // Check for "ftyp" signature at bytes 4-7
  const ftypMatch =
    bytes[4] === 0x66 && // 'f'
    bytes[5] === 0x74 && // 't'
    bytes[6] === 0x79 && // 'y'
    bytes[7] === 0x70 // 'p'

  if (!ftypMatch) return false

  // Convert bytes 8-11 to string for brand check
  const brandString = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11])

  // Check for HEIC brand identifiers
  const heicBrands = ['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1']
  const isHeicBrand = heicBrands.some((brand) => brandString.startsWith(brand))

  if (isHeicBrand) {
    console.log('🔍 Detected HEIC via magic bytes:', {
      brand: brandString,
      filename: 'checking content',
    })
  }

  return isHeicBrand
}

/**
 * Quick check: Detects HEIC by extension and MIME type only (synchronous)
 *
 * This is fast but won't catch renamed files (e.g., .heic renamed to .jpg)
 */
export const isHeicFile = (file: File): boolean => {
  const name = file.name.toLowerCase()
  const type = file.type.toLowerCase()

  return (
    name.endsWith('.heic') ||
    name.endsWith('.heif') ||
    type === 'image/heic' ||
    type === 'image/heif' ||
    type === 'image/heic-sequence' ||
    type === 'image/heif-sequence' ||
    type === '' // Some systems don't set MIME for HEIC
  )
}

/**
 * Enhanced HEIC detection with content inspection (asynchronous)
 *
 * This checks both file metadata (fast) and actual file content (slower but accurate).
 * Catches renamed HEIC files that have wrong extensions (e.g., .heic renamed to .jpg)
 *
 * @param file - The file to check
 * @returns Promise<boolean> - true if file is HEIC format
 */
export async function isHeicFileEnhanced(file: File): Promise<boolean> {
  // First, try quick checks (extension/MIME) - most common case
  const quickCheck = isHeicFile(file)

  if (quickCheck) {
    console.log('🔍 Detected HEIC via extension/MIME:', file.name)

    return true
  }

  // If quick check fails, inspect file content
  // This catches renamed HEIC files (.heic → .jpg)
  try {
    const header = await readFileHeader(file, 12)
    const hasHeicContent = hasHeicMagicBytes(header)

    if (hasHeicContent) {
      console.log('⚠️ Found HEIC content with wrong extension:', {
        filename: file.name,
        declaredType: file.type,
        actualFormat: 'HEIC',
      })
    }

    return hasHeicContent
  } catch (error) {
    console.warn('Failed to read file header for', file.name, error)

    return false // If can't read, assume not HEIC
  }
}

/**
 * Converts a single HEIC file to JPEG in the browser
 *
 * Uses libheif-js (via heic-decode) which supports ALL HEIC profiles
 * including MiPr (HEVC Main Progressive) from iOS 18+ / iPhone 14+.
 *
 * @param file - The HEIC file to convert
 * @param quality - JPEG quality (0-1), defaults to 0.9
 * @returns ConversionResult with converted file and metadata
 */
export const convertHeicToJpeg = async (file: File, quality: number = 0.9): Promise<ConversionResult> => {
  // Check if conversion is needed using enhanced detection
  const isHeic = await isHeicFileEnhanced(file)

  if (!isHeic) {
    return {
      file,
      wasConverted: false,
      originalName: file.name,
      originalSize: file.size,
    }
  }

  console.log('🔄 Converting HEIC to JPEG using libheif-js (heic-decode):', file.name)

  try {
    // Read file as ArrayBuffer then convert to Uint8Array
    const arrayBuffer = await file.arrayBuffer()
    const buffer = new Uint8Array(arrayBuffer)

    // Decode HEIC to raw image data using libheif-js
    const { width, height, data } = await decode({ buffer })

    // Create canvas and draw decoded image data
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('Failed to get canvas 2D context')
    }

    // Create ImageData from decoded RGBA buffer (data is already Uint8ClampedArray)
    const imageData = new ImageData(new Uint8ClampedArray(data), width, height)
    ctx.putImageData(imageData, 0, 0)

    // Convert canvas to JPEG Blob
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to convert canvas to blob'))
          }
        },
        'image/jpeg',
        quality
      )
    })

    // Create new File object with updated name
    const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg')
    const convertedFile = new File([blob], newFileName, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    })

    const sizeChange = ((convertedFile.size / file.size - 1) * 100).toFixed(1)
    const sizeDirection = parseFloat(sizeChange) > 0 ? 'increase' : 'reduction'

    console.log('✅ HEIC conversion successful (libheif-js with MiPr support):', {
      original: file.name,
      converted: newFileName,
      dimensions: `${width}x${height}`,
      originalSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      convertedSize: `${(convertedFile.size / 1024 / 1024).toFixed(2)} MB`,
      sizeChange: `${Math.abs(parseFloat(sizeChange))}% ${sizeDirection}`,
      supportsMiPr: true,
    })

    return {
      file: convertedFile,
      wasConverted: true,
      originalName: file.name,
      originalSize: file.size,
      convertedSize: convertedFile.size,
    }
  } catch (error) {
    console.error('❌ Failed to convert HEIC:', error)
    throw new Error(
      `Failed to convert HEIC image "${file.name}": ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }
}

/**
 * Converts multiple files, handling HEIC when detected
 *
 * @param files - Array of files to process
 * @param quality - JPEG quality (0-1), defaults to 0.9
 * @param onProgress - Optional callback for progress updates
 * @returns Array of conversion results
 */
export const convertMultipleFiles = async (
  files: File[],
  quality: number = 0.9,
  onProgress?: (current: number, total: number, filename: string) => void
): Promise<ConversionResult[]> => {
  const results: ConversionResult[] = []

  for (let i = 0; i < files.length; i++) {
    const file = files[i]

    // Report progress
    if (onProgress) {
      onProgress(i + 1, files.length, file.name)
    }

    // Convert if needed
    const result = await convertHeicToJpeg(file, quality)
    results.push(result)
  }

  return results
}

/**
 * Checks if any files in a FileList are HEIC format (quick check only)
 *
 * @param files - FileList to check
 * @returns true if at least one file is HEIC (by extension/MIME)
 */
export const hasHeicFiles = (files: FileList | File[]): boolean => {
  const fileArray = Array.isArray(files) ? files : Array.from(files)

  return fileArray.some(isHeicFile)
}

/**
 * Enhanced check: Detects HEIC files including renamed ones (async)
 *
 * Uses magic byte detection to catch files with wrong extensions.
 *
 * @param files - FileList or File array to check
 * @returns Promise<boolean> - true if at least one file is HEIC
 */
export const hasHeicFilesEnhanced = async (files: FileList | File[]): Promise<boolean> => {
  const fileArray = Array.isArray(files) ? files : Array.from(files)

  // Check each file in parallel for better performance
  const checks = fileArray.map((file) => isHeicFileEnhanced(file))
  const results = await Promise.all(checks)

  return results.some((isHeic) => isHeic)
}
